import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Award, FileText, Plus } from "lucide-react";

import type { AcademicYear } from "../../services/academicSetupService";
import { scholarshipService, type EligibleStudent, type Scholarship, type ScholarshipApplication } from "../../services/scholarshipService";
import { apiErrorMessage } from "../../utils/apiError";
import { todayIso } from "../../utils/dates";
import { Field } from "../FormLayout";
import { ReasonPanel } from "../inventory/InventoryParts";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, RecordFooter, type NoticeState } from "../register/RecordParts";
import { ApplyForm, AwardForm, ScholarshipForm } from "./ScholarshipForms";
import { PENDING, applicationConfig, applicationStatus, coverageLabel, day, reviewOptions, scholarshipConfig, scholarshipState, typeLabel, ugx } from "./scholarshipLogic";

/** What the school offers, with how many have applied and been awarded. */
export function ScholarshipsPanel({ rows, years, canManage, canApply, isLoading, isError, onRetry, onChanged, onApply }: {
  rows?: Scholarship[];
  years: AcademicYear[];
  canManage: boolean;
  canApply: boolean;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onChanged: () => void;
  onApply: (scholarship: Scholarship) => void;
}) {
  const today = todayIso();
  const config = scholarshipConfig(today);
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [editing, setEditing] = useState<Scholarship | "new" | null>(null);
  const save = useMutation({
    mutationFn: (data: Partial<Scholarship>) => (editing && editing !== "new" ? scholarshipService.updateScholarship(editing.id, data) : scholarshipService.createScholarship(data)),
    onSuccess: (saved) => { setNotice({ tone: "success", text: `${saved.name} ${editing === "new" ? "added" : "saved"}.` }); setEditing(null); onChanged(); },
  });
  const toggle = useMutation({
    mutationFn: ({ row, changes }: { row: Scholarship; changes: Partial<Scholarship> }) => scholarshipService.updateScholarship(row.id, changes),
    onSuccess: (saved) => { setNotice({ tone: "success", text: `${saved.name}: ${scholarshipState(saved, today).label.toLowerCase()}.` }); onChanged(); },
    onError: (error) => setNotice({ tone: "error", text: apiErrorMessage(error, "It could not be changed.") }),
  });
  const remove = useMutation({
    mutationFn: (row: Scholarship) => scholarshipService.deleteScholarship(row.id),
    onSuccess: (_, row) => { setViewing(null); setNotice({ tone: "success", text: `${row.name} deleted.` }); onChanged(); },
    onError: (error) => setNotice({ tone: "error", text: apiErrorMessage(error, "It could not be deleted.") }),
  });

  const columns: RegisterColumn<Scholarship>[] = [
    { header: "Scholarship", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name },
    { header: "Kind", cell: (row) => typeLabel(row.scholarship_type), exportValue: (row) => typeLabel(row.scholarship_type) },
    { header: "Covers", cell: (row) => coverageLabel(row.coverage), exportValue: (row) => coverageLabel(row.coverage) },
    { header: "Worth", cell: (row) => ugx(row.amount), exportValue: (row) => Number(row.amount), align: "right" },
    { header: "Apply by", cell: (row) => day(row.application_deadline), exportValue: (row) => day(row.application_deadline) },
    ...(canManage ? [
      { header: "Applied", cell: (row: Scholarship) => row.application_count ?? 0, exportValue: (row: Scholarship) => row.application_count ?? 0, align: "right" as const },
      { header: "To decide", cell: (row: Scholarship) => (row.pending_count ? <span className="font-semibold text-amber-700">{row.pending_count}</span> : 0), exportValue: (row: Scholarship) => row.pending_count ?? 0, align: "right" as const },
      { header: "Awarded", cell: (row: Scholarship) => (row.award_count ? `${row.award_count} · ${ugx(row.awarded_total)}` : "—"), exportValue: (row: Scholarship) => Number(row.awarded_total ?? 0) },
    ] : []),
    { header: "Sponsor", cell: (row) => row.sponsor || "—", exportValue: (row) => row.sponsor ?? "", exportOnly: true },
    { header: "Status", cell: (row) => <StatusDot {...scholarshipState(row, today)} />, exportValue: (row) => scholarshipState(row, today).label },
  ];
  const row = viewing ? rows?.find((item) => item.id === viewing) : undefined;
  const open = row ? scholarshipState(row, today).label === "Open" : false;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Scholarships"
        description={canManage ? "What the school offers, who can apply and by when, with how many families have applied and how much has been awarded." : "Scholarships open to applications. Open one to read who can apply, then apply for your child."}
        actions={canManage && <button type="button" className="btn btn-primary h-9 gap-1.5 text-sm" onClick={() => { save.reset(); setEditing("new"); }}><Plus className="h-4 w-4" /> New scholarship</button>}
        rows={rows}
        config={config}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => setViewing(item.id)}
        openLabel={() => (canManage ? "Manage" : "Read")}
        noun={["scholarship", "scholarships"]}
        exportName={{ file: "scholarships", title: "Scholarships" }}
        canExport={canManage}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canManage ? "No scholarships yet. Add the first." : "No scholarships are open to applications just now."}
      />

      {row && !editing && (
        <DetailsModal
          size="3xl"
          title={row.name}
          headline={{ label: `${typeLabel(row.scholarship_type)} · ${coverageLabel(row.coverage)}`, value: ugx(row.amount), note: `Apply by ${day(row.application_deadline)} · ${row.academic_year_name}` }}
          status={scholarshipState(row, today)}
          facts={[
            ["Sponsor", row.sponsor || "—"],
            ["Contact", [row.contact_person, row.contact_phone, row.contact_email].filter(Boolean).join(" · ") || "—"],
            ...(row.minimum_gpa ? [["Minimum GPA", row.minimum_gpa] as [string, string]] : []),
            ...(row.income_limit ? [["Household income up to", ugx(row.income_limit)] as [string, string]] : []),
            ...(canManage ? [["Applications", `${row.application_count ?? 0} (${row.pending_count ?? 0} to decide)`] as [string, string], ["Awarded", `${row.award_count ?? 0} · ${ugx(row.awarded_total)}`] as [string, string]] : []),
          ]}
          sections={[{ title: "About it", body: row.description }, { title: "Who can apply", body: row.eligibility_criteria }, { title: "Documents to attach", body: row.requirements }]}
          onClose={() => setViewing(null)}
          footer={
            canManage ? (
              <>
                {row.is_active && (
                  <button type="button" disabled={toggle.isPending} onClick={() => toggle.mutate({ row, changes: { is_available: !row.is_available } })} className="btn btn-outline">
                    {row.is_available ? "Close to applications" : "Reopen to applications"}
                  </button>
                )}
                <RecordFooter
                  canWrite
                  isActive={row.is_active}
                  inUse={row.application_count ? { applications: row.application_count } : {}}
                  busy={toggle.isPending || remove.isPending}
                  onDelete={() => window.confirm(`Delete ${row.name}?`) && remove.mutate(row)}
                  onToggle={() => toggle.mutate({ row, changes: { is_active: !row.is_active } })}
                  onEdit={() => { save.reset(); setEditing(row); }}
                  onClose={() => setViewing(null)}
                />
              </>
            ) : (
              <>
                <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
                {canApply && open && <button type="button" className="btn btn-primary gap-1.5" onClick={() => { setViewing(null); onApply(row); }}><FileText className="h-4 w-4" /> Apply</button>}
              </>
            )
          }
        />
      )}

      {editing && <ScholarshipForm record={editing === "new" ? undefined : editing} years={years} busy={save.isPending} error={save.isError ? apiErrorMessage(save.error, "The scholarship could not be saved.") : undefined} onSave={(data) => save.mutate(data)} onClose={() => setEditing(null)} />}
    </div>
  );
}

/** Families' applications: decide them, award the approved ones, or withdraw one. */
export function ApplicationsPanel({ rows, scholarships, learners, canManage, isParent, canApply, isLoading, isError, onRetry, onChanged, initialView, applyingFor, onApply, onCloseApply }: {
  rows?: ScholarshipApplication[];
  scholarships: Scholarship[];
  learners: EligibleStudent[];
  canManage: boolean;
  isParent: boolean;
  canApply: boolean;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onChanged: () => void;
  initialView?: string;
  /** null: not applying; 0: choose the scholarship in the form. */
  applyingFor: number | null;
  onApply: () => void;
  onCloseApply: () => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [awarding, setAwarding] = useState<ScholarshipApplication | null>(null);
  const done = (text: string) => { setNotice({ tone: "success", text }); onChanged(); };
  const apply = useMutation({
    mutationFn: scholarshipService.createApplication,
    onSuccess: (saved) => { onCloseApply(); done(`Application for ${saved.student_name} to ${saved.scholarship_name} submitted.`); },
  });
  const review = useMutation({
    mutationFn: scholarshipService.reviewApplication,
    onSuccess: (saved) => { setViewing(null); done(`${saved.student_name}: ${applicationStatus(saved.status).label.toLowerCase()}.`); },
  });
  const withdraw = useMutation({
    mutationFn: scholarshipService.cancelApplication,
    onSuccess: (saved) => { setViewing(null); done(`The application for ${saved.student_name} was withdrawn.`); },
  });
  const award = useMutation({
    mutationFn: scholarshipService.createAward,
    onSuccess: (saved) => { setAwarding(null); setViewing(null); done(`${saved.application_name} awarded ${ugx(saved.award_amount)} from ${saved.scholarship_name}.`); },
  });

  const columns: RegisterColumn<ScholarshipApplication>[] = [
    { header: "Learner", cell: (row) => <span><span className="font-medium text-gray-900">{row.student_name}</span> <span className="text-xs text-gray-500">{row.student_number}</span></span>, exportValue: (row) => row.student_name },
    { header: "Student ID", cell: (row) => row.student_number ?? "", exportValue: (row) => row.student_number ?? "", exportOnly: true },
    { header: "Class", cell: (row) => row.class_name || "—", exportValue: (row) => row.class_name ?? "" },
    { header: "Scholarship", cell: (row) => row.scholarship_name, exportValue: (row) => row.scholarship_name },
    { header: "Applied", cell: (row) => day(row.application_date), exportValue: (row) => day(row.application_date) },
    { header: "Status", cell: (row) => <StatusDot {...applicationStatus(row.status)} />, exportValue: (row) => applicationStatus(row.status).label },
    { header: "Award", cell: (row) => (row.award_id ? "Awarded" : row.status === "approved" ? <span className="text-amber-700">Not yet</span> : "—"), exportValue: (row) => (row.award_id ? "Awarded" : "") },
    { header: "Decision", cell: (row) => row.decision_notes ?? "", exportValue: (row) => row.decision_notes ?? "", exportOnly: true },
  ];
  const row = viewing ? rows?.find((item) => item.id === viewing) : undefined;
  const options = row && canManage ? reviewOptions(row.status) : [];
  const openScholarships = scholarships.filter((item) => item.is_open);

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={initialView}
        title={isParent ? "Your applications" : "Applications"}
        description={isParent ? "Your applications and the school's decision on each, with its reason." : "Applications from families, oldest first. Open one to read the family's case and record a decision; approved applications can then be given an award."}
        actions={canApply && <button type="button" className="btn btn-primary h-9 gap-1.5 text-sm" disabled={!openScholarships.length} title={openScholarships.length ? undefined : "No scholarship is open to applications"} onClick={onApply}><FileText className="h-4 w-4" /> {isParent ? "Apply" : "New application"}</button>}
        rows={rows}
        config={applicationConfig}
        initialState={initialView ? { view: initialView } : undefined}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => setViewing(item.id)}
        openLabel={(item) => (canManage && PENDING.includes(item.status) ? "Decide" : canManage && item.status === "approved" && !item.award_id ? "Award" : "Open")}
        noun={["application", "applications"]}
        exportName={{ file: "scholarship-applications", title: "Scholarship Applications" }}
        canExport={canManage}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={isParent ? "You have not applied for a scholarship yet." : "No applications yet."}
      />

      {row && !awarding && (
        <DetailsModal
          size="3xl"
          title={row.student_name}
          headline={{ label: row.scholarship_name, value: applicationStatus(row.status).label, note: `Applied ${day(row.application_date)}${row.decision_date ? ` · decided ${day(row.decision_date)}` : ""}` }}
          status={applicationStatus(row.status)}
          facts={[["Student ID", row.student_number ?? "—"], ["Class", row.class_name || "—"], ["Scholarship worth", ugx(row.scholarship_amount)], ["Reviewed by", row.reviewed_by_name || "—"]]}
          sections={[
            { title: "Decision", body: row.decision_notes, tone: row.status === "rejected" ? "danger" : "plain" },
            { title: "Personal statement", body: row.personal_statement },
            { title: "Financial need", body: row.financial_need },
            { title: "Achievements", body: row.academic_achievements },
            { title: "Clubs, sport and activities", body: row.extracurricular_activities },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canManage && row.status === "approved" && !row.award_id && <button type="button" className="btn btn-primary gap-1.5" onClick={() => { award.reset(); setAwarding(row); }}><Award className="h-4 w-4" /> Make the award</button>}
            </>
          }
        >
          {options.length > 0 && <DecisionPanel application={row} options={options} busy={review.isPending} error={review.error} onDecide={(status, notes) => review.mutate({ id: row.id, status, notes })} />}
          {isParent && PENDING.includes(row.status) && (
            <ReasonPanel title="Withdraw the application" description="The school stops considering it." placeholder="Why you are withdrawing (optional detail helps the school)" context={{ key: "scholarship.note", params: { kind: "withdrawal", scholarship: row.scholarship, student: row.student } }} button="Withdraw application" danger busy={withdraw.isPending} error={withdraw.error} errorText="The application could not be withdrawn." onSubmit={(reason) => withdraw.mutate({ id: row.id, reason })} />
          )}
        </DetailsModal>
      )}

      {awarding && <AwardForm application={awarding} busy={award.isPending} error={award.isError ? apiErrorMessage(award.error, "The award could not be made.") : undefined} onSave={(data) => award.mutate(data)} onClose={() => setAwarding(null)} />}
      {applyingFor !== null && (
        <ApplyForm
          scholarships={openScholarships}
          learners={learners}
          scholarshipId={applyingFor || undefined}
          isParent={isParent}
          busy={apply.isPending}
          error={apply.isError ? apiErrorMessage(apply.error, "The application could not be submitted.") : undefined}
          onSave={(data) => apply.mutate(data)}
          onClose={onCloseApply}
        />
      )}
    </div>
  );
}

function DecisionPanel({ application, options, busy, error, onDecide }: {
  application: ScholarshipApplication;
  options: ReturnType<typeof reviewOptions>;
  busy: boolean;
  error: unknown;
  onDecide: (status: string, notes: string) => void;
}) {
  const [decision, setDecision] = useState(options[0].value);
  const chosen = options.find((option) => option.value === decision);
  if (decision === "under_review") {
    return (
      <section className="flex flex-wrap items-end justify-between gap-3 rounded-lg border border-gray-200 p-4">
        <Field label="Decision">
          <select value={decision} onChange={(event) => setDecision(event.target.value)} className="input mt-1">{options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select>
        </Field>
        <button type="button" disabled={busy} className="btn btn-primary" onClick={() => onDecide("under_review", "")}>Take under review</button>
      </section>
    );
  }
  return (
    <ReasonPanel
      title="Decide the application"
      description={chosen?.help}
      label="Reason"
      placeholder="What the decision rests on; the family sees it"
      context={{ key: "scholarship.note", params: { kind: "decision", id: application.id, decision } }}
      button={chosen?.label ?? "Save"}
      danger={decision === "rejected"}
      busy={busy}
      error={error}
      errorText="The decision could not be recorded."
      onSubmit={(notes) => onDecide(decision, notes)}
    >
      <Field label="Decision">
        <select value={decision} onChange={(event) => setDecision(event.target.value)} className="input mt-1">{options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select>
      </Field>
    </ReasonPanel>
  );
}
